 function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }import styled from '@emotion/styled'
import graphql from 'babel-plugin-relay/macro'
import React, { useEffect, useMemo, useRef, useState} from 'react'
import {commitLocalUpdate, useFragment} from 'react-relay'
import useAtmosphere from '../../hooks/useAtmosphere'
import useEventCallback from '../../hooks/useEventCallback'
import useExpandedReflections from '../../hooks/useExpandedReflections'
import {
  DragAttribute,
  ElementWidth,
  ReflectionStackPerspective,
  Times
} from '../../types/constEnums'
import {GROUP} from '../../utils/constants'




import ReflectionGroupHeader from '../ReflectionGroupHeader'
import ExpandedReflectionStack from '../RetroReflectPhase/ExpandedReflectionStack'
import DraggableReflectionCard from './DraggableReflectionCard'
import useSpotlightReflectionGroup from './useSpotlightReflectionGroup'

const CardStack = styled('div')({
  position: 'relative'
})

export const getCardStackPadding = (count) => {
  return Math.max(0, Math.min(3, count) - 1) * ReflectionStackPerspective.Y
}

const Group = styled('div')(
  ({staticReflectionCount, isSpotlightSource}) => ({
    height: 'max-content',
    position: 'relative',
    paddingTop: ElementWidth.REFLECTION_CARD_PADDING,
    paddingBottom: isSpotlightSource
      ? ElementWidth.REFLECTION_CARD_PADDING
      : ElementWidth.REFLECTION_CARD_PADDING + getCardStackPadding(staticReflectionCount),
    transition: `padding-bottom ${Times.REFLECTION_DROP_DURATION}ms`
  })
)

const ReflectionWrapper = styled('div')




(({staticIdx, isDropping, groupCount, isHiddenSpotlightSource}) => {
  const isHidden = staticIdx === -1 || isDropping || isHiddenSpotlightSource
  const multiple = Math.max(0, Math.min(staticIdx, 2))
  const scaleX =
    (ElementWidth.REFLECTION_CARD - ReflectionStackPerspective.X * multiple * 2) /
    ElementWidth.REFLECTION_CARD
  const translateY = ReflectionStackPerspective.Y * multiple
  return {
    position: staticIdx === 0 || (staticIdx === -1 && groupCount === 1) ? 'relative' : 'absolute',
    bottom: 0,
    left: 0,
    outline: 0,
    transform: `translateY(${translateY}px) scaleX(${scaleX})`,
    transition: isHidden ? undefined : `transform ${Times.REFLECTION_DROP_DURATION}ms`,
    visibility: isHidden ? 'hidden' : undefined,
    zIndex: 3 - multiple
  }
})













const ReflectionGroup = (props) => {
  const {
    meetingRef,
    openSpotlight,
    phaseRef,
    reflectionGroupRef,
    swipeColumn,
    dataCy,
    reflectionIdsToHide,
    isSpotlightEntering,
    showDragHintAnimation
  } = props
  const meeting = useFragment(
    graphql`
      fragment ReflectionGroup_meeting on RetrospectiveMeeting {
        ...DraggableReflectionCard_meeting
        ...ReflectionGroupHeader_meeting
        ...useSpotlightResults_meeting
        id
        localPhase {
          phaseType
        }
        localStage {
          isComplete
        }
        isViewerDragInProgress
        spotlightGroup {
          id
          reflections {
            isViewerDragging
          }
        }
        spotlightSearchQuery
      }
    `,
    meetingRef
  )

  const reflectionGroup = useFragment(
    graphql`
      fragment ReflectionGroup_reflectionGroup on RetroReflectionGroup {
        ...ReflectionGroupHeader_reflectionGroup
        promptId
        id
        sortOrder
        titleIsUserDefined
        title
        reflections {
          ...DraggableReflectionCard_reflection
          ...DraggableReflectionCard_staticReflections
          ...ReflectionCard_reflection
          id
          promptId
          sortOrder
          isViewerDragging
          isDropping
          isEditing
          remoteDrag {
            dragUserId
            isSpotlight
          }
          plaintextContent
        }
        isExpanded
      }
    `,
    reflectionGroupRef
  )
  const groupRef = useRef(null)
  const {localPhase, localStage, spotlightGroup} = meeting
  const {phaseType} = localPhase
  const {isComplete} = localStage
  const {id: reflectionGroupId, titleIsUserDefined} = reflectionGroup
  const spotlightGroupId = _optionalChain([spotlightGroup, 'optionalAccess', _ => _.id])
  const isSpotlightSrcGroup = spotlightGroupId === reflectionGroupId
  const isBehindSpotlight = !!(spotlightGroupId && openSpotlight)
  const {isRemoteSpotlightSrc, disableDrop, visibleReflections} = useSpotlightReflectionGroup(
    meeting,
    reflectionGroup,
    isBehindSpotlight,
    reflectionIdsToHide
  )
  const titleInputRef = useRef(null)
  const expandedTitleInputRef = useRef(null)
  const headerRef = useRef(null)
  const staticReflections = useMemo(() => {
    return visibleReflections.filter(
      (reflection) =>
        !reflection.isViewerDragging && (!reflection.remoteDrag || reflection.isDropping)
    )
  }, [visibleReflections])
  const stackRef = useRef(null)
  const {setItemsRef, scrollRef, bgRef, modalHeaderRef, portal, portalStatus, collapse, expand} =
    useExpandedReflections(groupRef, stackRef, visibleReflections.length, headerRef)
  const atmosphere = useAtmosphere()
  const [isEditing, thisSetIsEditing] = useState(false)
  const isDragPhase = phaseType === 'group' && !isComplete
  const setIsEditing = (isEditing) => {
    thisSetIsEditing(isEditing)
    const [firstReflection] = staticReflections
    if (!firstReflection) return
    const {id: firstReflectionId} = firstReflection
    commitLocalUpdate(atmosphere, (store) => {
      const reflection = store.get(firstReflectionId)
      reflection.setValue(isEditing, 'isEditing')
    })
  }

  const watchForClick = useEventCallback((e) => {
    const isClickOnGroup = e.composedPath().find((el) => el === groupRef.current)
    if (!isClickOnGroup) {
      document.removeEventListener('click', watchForClick)
      setIsEditing(false)
    }
  })
  const onClick = () => {
    if (isEditing || isRemoteSpotlightSrc) return
    const wasDrag = staticReflections.some((reflection) => reflection.isDropping)
    if (wasDrag) return
    if (visibleReflections.length === 1) {
      if (!isDragPhase) return
      setIsEditing(true)
      document.addEventListener('click', watchForClick)
    } else {
      expand()
    }
  }

  useEffect(() => {
    return () => {
      document.removeEventListener('click', watchForClick)
    }
  }, [])

  const showHeader =
    (phaseType !== GROUP || titleIsUserDefined || visibleReflections.length > 1 || isEditing) &&
    !isSpotlightSrcGroup

  return (
    React.createElement(React.Fragment, null
      , portal(
        React.createElement(ExpandedReflectionStack, {
          header: 
            React.createElement(ReflectionGroupHeader, {
              isExpanded: true,
              ref: modalHeaderRef,
              meeting: meeting,
              portalStatus: portalStatus,
              reflectionGroup: reflectionGroup,
              titleInputRef: expandedTitleInputRef,}
            )
          ,
          phaseRef: phaseRef,
          staticReflections: staticReflections,
          reflections: visibleReflections,
          scrollRef: scrollRef,
          bgRef: bgRef,
          setItemsRef: setItemsRef,
          closePortal: collapse,
          meeting: meeting,
          reflectionGroupId: reflectionGroupId,
          openSpotlight: openSpotlight,
          isBehindSpotlight: isBehindSpotlight,}
        )
      )
      , React.createElement(Group, {
        ...(disableDrop ? null : {[DragAttribute.DROPPABLE]: reflectionGroupId}),
        ref: groupRef,
        staticReflectionCount: staticReflections.length,
        isSpotlightSource: isSpotlightSrcGroup && !isBehindSpotlight,
        'data-cy': dataCy,}

        , showHeader && (
          React.createElement(ReflectionGroupHeader, {
            dataCy: `${dataCy}-header`,
            ref: headerRef,
            meeting: meeting,
            reflectionGroup: reflectionGroup,
            portalStatus: portalStatus,
            titleInputRef: titleInputRef,}
          )
        )
        , React.createElement(CardStack, { 'data-cy': `${dataCy}-stack`, ref: stackRef, onClick: onClick,}
          , visibleReflections.map((reflection) => {
            const staticIdx = staticReflections.indexOf(reflection)
            const {id: reflectionId, isDropping} = reflection
            return (
              React.createElement(ReflectionWrapper, {
                'data-cy': `${dataCy}-card-${staticIdx}`,
                key: reflectionId,
                groupCount: visibleReflections.length,
                staticIdx: staticIdx,
                isDropping: isDropping,
                isHiddenSpotlightSource: isSpotlightSrcGroup && isBehindSpotlight,}

                , React.createElement(DraggableReflectionCard, {
                  dataCy: `${dataCy}-card-${staticIdx}`,
                  key: reflection.id,
                  staticIdx: staticIdx,
                  isClipped: staticIdx > 0 || isRemoteSpotlightSrc,
                  isDraggable: staticIdx === 0 && !isRemoteSpotlightSrc,
                  meeting: meeting,
                  openSpotlight: openSpotlight,
                  reflection: reflection,
                  staticReflections: staticReflections,
                  swipeColumn: swipeColumn,
                  isSpotlightEntering: !!isSpotlightEntering,
                  showDragHintAnimation: showDragHintAnimation,}
                )
              )
            )
          })
        )
      )
    )
  )
}

export default ReflectionGroup
